import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Consultation,
  FormQuestionResponse,
  QuestionOptionType,
  AnswerValue,
} from "@/types/consultation";
import { Control, ControllerRenderProps } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";

interface DynamicQuestionRendererProps {
  question: FormQuestionResponse;
  control: Control<Consultation>;
  index: number;
}



const isNumericQuestion = (text: string) =>
  /how many|per day|per night|per week|scale of|rate your|rate .*(sleep|stress)|hours|frequency/i.test(
    text
  );

const getStepperUnit = (text: string) => {
  if (/hour|sleep|night/i.test(text)) return "hrs";
  if (/per week|days per/i.test(text)) return "×/wk";
  return "";
};

export const formatAnswerForSubmission = (
  answer: any,
  questionType: string
): any => {
  if (answer === undefined || answer === null || answer === "") {
    return null;
  }

  switch (questionType) {
    case "toggle":
      if (typeof answer === "boolean") return answer;
      if (answer === "true" || answer === 1 || answer === "1") return true;
      if (answer === "false" || answer === 0 || answer === "0") return false;
      return Boolean(answer);
    case "text":
    case "number":
      const stringValue = String(answer).trim();
      if (stringValue === "") return null;
      return stringValue;
    case "textarea":
      const textareaValue = String(answer).trim();
      return textareaValue === "" ? null : textareaValue;
    case "radio":
    case "select":
      return String(answer).trim();
    case "checkbox":
      if (Array.isArray(answer)) {
        const filtered = answer
          .filter(v => v !== undefined && v !== null && v !== "")
          .map(v => String(v).trim());
        
        return filtered.length > 0 ? filtered : null;
      }
      if (answer) {
        return [String(answer).trim()];
      }
      return null;
    case "date":
    case "time":
      return String(answer).trim();
    default:
      return String(answer).trim();
  }
};

const DynamicQuestionRenderer = ({
  question,
  control,
  index,
}: DynamicQuestionRendererProps) => {
  const { id, question_text, question_type, options, is_required } = question;
  const name = `answers.${id}` as `answers.${string}`;

  // Radio/select with ≤3 options → inline (fits beside question label)
  // Radio/select with 4+ options → vertical (label on top, pills below)
  const manyOptions =
    (question_type === "radio" || question_type === "select") &&
    (options?.length ?? 0) > 3;

  // These types render vertically (label on top, control below)
  const isVertical =
    question_type === "checkbox" ||
    question_type === "textarea" ||
    manyOptions;

  // Use number stepper for "number" type OR text questions that ask for a quantity
  const useNumberStepper =
    question_type === "number" ||
    (question_type === "text" && isNumericQuestion(question_text));

  const unit = getStepperUnit(question_text);

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required:
          is_required && question_type !== "toggle"
            ? "This field is required"
            : false,
      }}
      render={({ field, fieldState: { error } }) => (
        <FormItem className="w-full">
          <div 
            className={cn(
              "rounded-xl border bg-white px-4 py-3",
              isVertical
                ? "flex flex-col gap-3"
                : "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:min-h-[56px]",
              error ? "border-red-400" : "border-gray-200"
            )}
          >
            {/* Question label */}
            <span className="text-sm text-gray-700 font-medium leading-snug flex-1 min-w-0">
              {index}. {question_text}
              {is_required && <span className="text-red-500 ml-0.5">*</span>}
              {question_type === "textarea" &&
                options &&
                options.length > 0 && (
                  <>
                    <br />
                    <span className="text-gray-400 text-xs font-normal">
                      (
                      {options
                        .map((opt: QuestionOptionType) =>
                          typeof opt === "string" ? opt : opt.label
                        )
                        .join(", ")}
                      )
                    </span>
                  </>
                )}
            </span>

            <FormControl>
              {isVertical
                ? renderVertical(question_type, field, options)
                : renderInline(question_type, field, options, useNumberStepper, unit)}
            </FormControl>
            {error && (
              <FormMessage className="text-xs pl-1 mt-1" />
            )}
          </div>
        </FormItem>
      )}
    />
  );
};

// ─── Inline controls (label left, control right) ─────────────────────────────

const renderInline = (
  type: string,
  field: ControllerRenderProps<Consultation, `answers.${string}`>,
  options: QuestionOptionType[] | null | undefined,
  useNumberStepper: boolean,
  unit: string
) => {
  switch (type) {
    case "toggle":
      return <TogglePills field={field} />;

    case "number":
      return <NumberStepper field={field} unit={unit} />;

    case "text":
    case "email":
      if (useNumberStepper) {
        return <NumberStepper field={field} unit={unit} />;
      }
      return (
        <Input
          placeholder="Your answer"
          {...field}
          value={(field.value as string | number) || ""}
          className="bg-white w-full sm:w-40 text-sm h-9"
        />
      );

    // radio/select with ≤3 options → inline pills
    case "radio":
    case "select":
      return <PillGroup field={field} options={options} multi={false} />;

    default:
      return (
        <Input
          {...field}
          value={(field.value as string | number) || ""}
          className="bg-white w-full sm:w-40 text-sm h-9"
        />
      );
  }
};

// ─── Vertical controls (label top, control bottom) ───────────────────────────

const renderVertical = (
  type: string,
  field: ControllerRenderProps<Consultation, `answers.${string}`>,
  options: QuestionOptionType[] | null | undefined
) => {
  switch (type) {
    case "checkbox":
      return <PillGroup field={field} options={options} multi={true} />;

    // radio/select with 4+ options → vertical pills
    case "radio":
    case "select":
      return <PillGroup field={field} options={options} multi={false} />;

    case "textarea":
      return (
        <Textarea
          placeholder="Your answer here"
          {...field}
          value={(field.value as string) || ""}
          className="bg-white min-h-[72px] text-sm resize-none border-gray-200 focus-visible:ring-1"
        />
      );

    default:
      return null;
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/** No / Yes toggle pill buttons */
const TogglePills = ({
  field,
}: {
  field: ControllerRenderProps<Consultation, `answers.${string}`>;
}) => {
  const isYes =
    field.value === true || field.value === "true" || field.value === 1;

  return (
    <div className="flex items-center rounded-full border border-gray-200 overflow-hidden shrink-0 w-fit">
      <button
        type="button"
        onClick={() => field.onChange(false)}
        className={cn(
          "px-4 py-1.5 text-sm font-medium transition-all",
          !isYes
            ? "bg-red-500 text-white"
            : "bg-white text-gray-500 hover:bg-gray-50"
        )}
      >
        No
      </button>
      <button
        type="button"
        onClick={() => field.onChange(true)}
        className={cn(
          "px-4 py-1.5 text-sm font-medium transition-all",
          isYes
            ? "bg-emerald-500 text-white"
            : "bg-white text-gray-500 hover:bg-gray-50"
        )}
      >
        Yes
      </button>
    </div>
  );
};

/** − value unit + stepper for number questions */
const NumberStepper = ({
  field,
  unit,
}: {
  field: ControllerRenderProps<Consultation, `answers.${string}`>;
  unit: string;
}) => {
  const value = Number(field.value) || 0;
  const MAX_VALUE = 24;

  return (
    <div className="flex items-center gap-2 w-fit">
      <button
        type="button"
        onClick={() => field.onChange(Math.max(0, value - 1))}
        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <span className="min-w-[2rem] text-center text-sm font-semibold text-gray-800">
        {value}
      </span>
      <button
        type="button"
        onClick={() => field.onChange(Math.min(MAX_VALUE, value + 1))}
        className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/** Unified pill group — works for checkbox (multi) and radio/select (single) */
const PillGroup = ({
  field,
  options,
  multi,
}: {
  field: ControllerRenderProps<Consultation, `answers.${string}`>;
  options: QuestionOptionType[] | null | undefined;
  multi: boolean;
}) => {
  if (!options || options.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt: QuestionOptionType, idx: number) => {
        const label = typeof opt === "string" ? opt : opt.label;
        const value = typeof opt === "string" ? opt : opt.value;

        const isSelected = multi
          ? Array.isArray(field.value) &&
            (field.value as AnswerValue[]).includes(value)
          : field.value === value || field.value === String(value);

        const toggle = () => {
          if (multi) {
            const current = Array.isArray(field.value)
              ? (field.value as AnswerValue[])
              : [];
            if (isSelected) {
              field.onChange(current.filter((v: AnswerValue) => v !== value));
            } else {
              field.onChange([...current, value]);
            }
          } else {
            field.onChange(value);
          }
        };

        return (
          <button
            key={idx}
            type="button"
            onClick={toggle}
            className={cn(
              "px-3 py-1.5 rounded-full border text-sm font-medium transition-all",
              isSelected
                ? "bg-emerald-500 text-white border-emerald-500"
                : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default DynamicQuestionRenderer;